import cn from 'classnames'
import type { FC } from 'react'
import type { YjsSyncState } from './hooks/use-yjs-sync-state.hook.js'
import styles from './sync-status-dot.module.css'

export interface SyncStatusDotProps {
  state: YjsSyncState
  /** Provider connection. When undefined, treated as not yet known (= syncing). */
  connected?: boolean
}

interface Tone {
  className: string
  label: string
  description: string
}

const toneFor = (state: YjsSyncState, connected: boolean | undefined): Tone => {
  // Disconnected from the server: local edits stack up
  if (connected === false) {
    return {
      className: styles.offline,
      label: 'Offline',
      description:
        state.unsyncedUpdateCount > 0
          ? `${state.unsyncedUpdateCount} change${state.unsyncedUpdateCount === 1 ? '' : 's'} will sync when you reconnect`
          : 'Offline — changes saved locally',
    }
  }
  // Connected but not yet server-acked: syncing
  if (!state.serverSynced) {
    return {
      className: styles.syncing,
      label: 'Syncing',
      description: 'Syncing with server…',
    }
  }
  // Fully synced
  return {
    className: styles.synced,
    label: 'Synced',
    description: 'All changes saved',
  }
}

export const SyncStatusDot: FC<SyncStatusDotProps> = props => {
  const { state, connected } = props
  const tone = toneFor(state, connected)
  return (
    <span
      role="status"
      aria-label={`${tone.label}: ${tone.description}`}
      title={tone.description}
      className={cn(styles.dot, tone.className)}
    />
  )
}
